import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Loader2, Plus, Sparkles, Share2, Trash2, ChevronRight, ChevronLeft, X, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_LABEL = {
  sourced: "Sourced", screening: "Screening", submitted: "Submitted",
  interview: "Interview", offer: "Offer", hired: "Hired", rejected: "Rejected",
};

export default function SearchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useMeta();
  const stages = meta.pipeline_stages;
  const [search, setSearch] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [allCandidates, setAllCandidates] = useState([]);
  const [candSearch, setCandSearch] = useState("");
  const [ai, setAi] = useState({ open: false, loading: false, title: "", output: "" });

  const load = useCallback(() => api.get(`/searches/${id}`).then(({ data }) => setSearch(data)), [id]);
  useEffect(() => { load(); }, [load]);

  const openAdd = async () => {
    const { data } = await api.get("/candidates");
    setAllCandidates(data);
    setAddOpen(true);
  };

  const removeSearch = async () => {
    if (!window.confirm(`Delete the search "${search.title}"? This removes the search and its entire candidate pipeline. This cannot be undone.`)) return;
    try {
      await api.delete(`/searches/${id}`);
      toast.success("Search deleted");
      navigate("/app/searches");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addCandidate = async (candidate_id) => {
    try {
      await api.post(`/searches/${id}/candidates`, { candidate_id });
      toast.success("Added to pipeline");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const moveStage = async (sc, dir) => {
    const idx = stages.indexOf(sc.stage);
    const next = stages[idx + dir];
    if (!next) return;
    await api.patch(`/searches/${id}/candidates/${sc.id}`, { stage: next });
    load();
  };

  const toggleShare = async (sc) => {
    const willShare = !sc.shared_with_client;
    let acknowledge = false;
    if (willShare && sc.candidate?.visibility_status === "confidential") {
      const name = sc.candidate?.full_name || "This candidate";
      if (!window.confirm(`⚠️ ${name} is marked Confidential. Sharing will expose their profile to the client. Continue?`)) return;
      acknowledge = true;
    }
    try {
      await api.post(`/searches/${id}/share-candidate/${sc.id}`, null, { params: { acknowledge } });
      load();
    } catch (err) {
      if (err.response?.status === 409) {
        const name = sc.candidate?.full_name || "This candidate";
        if (window.confirm(`⚠️ ${name} is marked Confidential. Share with the client anyway?`)) {
          await api.post(`/searches/${id}/share-candidate/${sc.id}`, null, { params: { acknowledge: true } });
          load();
        }
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    }
  };

  const removeCard = async (sc) => {
    await api.delete(`/searches/${id}/candidates/${sc.id}`);
    toast.success("Removed from pipeline");
    load();
  };

  const runAI = async (type) => {
    const endpoints = { match: "/ai/candidate-match", analysis: "/ai/search-analysis" };
    const titles = { match: "AI Candidate Match", analysis: "AI Search Brief Analysis" };
    setAi({ open: true, loading: true, title: titles[type], output: "" });
    try {
      const { data } = await api.post(endpoints[type], { search_id: id });
      setAi({ open: true, loading: false, title: titles[type], output: data.output_data });
    } catch (err) {
      setAi({ open: false, loading: false, title: "", output: "" });
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!search)
    return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const pipelineIds = new Set(search.pipeline.map((p) => p.candidate_id));
  const availableCandidates = allCandidates.filter(
    (c) => !pipelineIds.has(c.id) && c.full_name.toLowerCase().includes(candSearch.toLowerCase())
  );

  return (
    <div>
      <PageHeader overline={`${search.organization_name} · ${search.role_type}`} title={search.title}>
        <Button variant="ghost" onClick={() => runAI("analysis")} data-testid="ai-analysis-btn" className="rounded-none text-muted-foreground hover:text-[#09090B]">
          <Sparkles className="h-4 w-4 mr-1" /> Analyze brief
        </Button>
        <Button variant="ghost" onClick={() => runAI("match")} data-testid="ai-match-btn" className="rounded-none text-muted-foreground hover:text-[#09090B]">
          <Sparkles className="h-4 w-4 mr-1" /> Match
        </Button>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} data-testid="add-candidate-btn" className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
              <Plus className="h-4 w-4 mr-1" /> Add candidate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F4F4F5] border-black/10 rounded-none max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Add to pipeline</DialogTitle></DialogHeader>
            <Input value={candSearch} onChange={(e) => setCandSearch(e.target.value)} placeholder="Search candidates…" className="bg-white border-black/10 rounded-none" />
            <div className="max-h-80 overflow-y-auto space-y-px mt-2">
              {availableCandidates.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No candidates available. Add candidates first.</p>}
              {availableCandidates.map((c) => (
                <button key={c.id} onClick={() => addCandidate(c.id)} data-testid={`add-cand-${c.id}`}
                  className="w-full flex items-center justify-between p-3 border-b border-black/5 hover:bg-black/5 transition-colors text-left">
                  <div>
                    <p className="font-medium text-sm">{c.full_name}</p>
                    <p className="overline text-[0.55rem]">{c.headline || "—"}</p>
                  </div>
                  <Plus className="h-4 w-4 text-primary" />
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        <Button variant="ghost" onClick={removeSearch} data-testid="search-delete-btn" className="rounded-none text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="h-4 w-4" /></Button>
        <Button variant="ghost" onClick={() => navigate("/app/searches")} className="rounded-none"><ArrowLeft className="h-4 w-4" /></Button>
      </PageHeader>

      {/* Brief bar */}
      {(search.search_brief || search.compensation_range || search.location) && (
        <div className="px-8 py-4 border-b border-black/10 bg-white flex flex-wrap gap-x-10 gap-y-2">
          {search.location && <div><span className="overline text-[0.55rem] mr-2">Location</span><span className="text-sm">{search.location}</span></div>}
          {search.compensation_range && <div><span className="overline text-[0.55rem] mr-2">Comp</span><span className="text-sm">{search.compensation_range}</span></div>}
          {search.hiring_type && <div><span className="overline text-[0.55rem] mr-2">Model</span><span className="text-sm capitalize">{search.hiring_type}</span></div>}
        </div>
      )}

      {/* Kanban */}
      <div className="p-6 overflow-x-auto">
        <div className="flex gap-px bg-black/10 border border-black/10 min-w-max">
          {stages.map((stage) => {
            const cards = search.pipeline.filter((p) => p.stage === stage);
            return (
              <div key={stage} className="w-64 bg-white flex flex-col" data-testid={`col-${stage}`}>
                <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between sticky top-0 bg-white">
                  <span className="overline text-[0.55rem]">{STAGE_LABEL[stage]}</span>
                  <span className="font-heading font-bold text-sm">{cards.length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[300px]">
                  {cards.map((sc) => {
                    const c = sc.candidate || {};
                    const idx = stages.indexOf(stage);
                    return (
                      <div key={sc.id} data-testid={`card-${sc.id}`}
                        className="group bg-[#F4F4F5] border border-black/10 p-3 hover:-translate-y-1 transition-transform duration-150">
                        <div className="flex items-start justify-between">
                          <button onClick={() => navigate(`/app/candidates/${c.id}`)} className="text-left">
                            <p className="font-medium text-sm hover:text-primary transition-colors">{c.full_name}</p>
                            <p className="overline text-[0.5rem] mt-0.5">{c.headline || "—"}</p>
                            {c.visibility_status === "confidential" && (
                              <span data-testid={`confidential-${sc.id}`} className="inline-flex items-center gap-1 mt-1 text-[0.5rem] font-mono uppercase tracking-wider text-[#F5A623] border border-[#F5A623]/40 px-1">
                                <EyeOff className="h-2.5 w-2.5" /> Confidential
                              </span>
                            )}
                          </button>
                          {sc.shared_with_client && <Share2 className="h-3 w-3 text-primary shrink-0" />}
                        </div>
                        <div className="flex items-center justify-between mt-3 opacity-40 group-hover:opacity-100 transition-opacity">
                          <div className="flex gap-1">
                            <button disabled={idx === 0} onClick={() => moveStage(sc, -1)} className="p-1 hover:bg-black/10 disabled:opacity-20" title="Back">
                              <ChevronLeft className="h-3.5 w-3.5" />
                            </button>
                            <button disabled={idx === stages.length - 1} onClick={() => moveStage(sc, 1)} className="p-1 hover:bg-black/10 disabled:opacity-20" title="Advance">
                              <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => toggleShare(sc)} data-testid={`share-${sc.id}`} className={`p-1 hover:bg-black/10 ${sc.shared_with_client ? "text-primary" : ""}`} title="Share with client">
                              <Share2 className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => removeCard(sc)} className="p-1 hover:bg-black/10 hover:text-destructive" title="Remove">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI output dialog */}
      <Dialog open={ai.open} onOpenChange={(o) => setAi({ ...ai, open: o })}>
        <DialogContent className="bg-[#F4F4F5] border-black/10 rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> {ai.title}
            </DialogTitle>
          </DialogHeader>
          {ai.loading ? (
            <div className="flex flex-col items-center py-16 gap-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="overline">Claude is analyzing…</p>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap font-mono text-sm text-[#27272A] leading-relaxed border-l-2 border-primary pl-4">{ai.output}</pre>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
